<template>
    <modal name="estimate-cost" height="auto" ref="estimate-cost" :width="isCompare ? 1160 : 600" :pivotY="0.2" :pivotX="0.5" @before-open="beforeOpen" @opened="opened">
        <template v-if="modalLoading">
            <div class="preloader">
                <div class="preloader-wrapper small active">
                    <div class="spinner-layer">
                        <div class="circle-clipper left">
                            <div class="circle"></div>
                        </div>
                        <div class="gap-patch">
                            <div class="circle"></div>
                        </div>
                        <div class="circle-clipper right">
                            <div class="circle"></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="modal-content compare-mode" v-if="isCompare">
                <div class="modal-header">Estimate Print Costs Comparison</div>
                <form class="modal-form">
                    <div class="row">
                        <div class="input-field col s6 cost">
                            <input type="number"
                                  min="0"
                                  name="costPerHour"
                                  id="costPerHour"
                                  v-model="formData.costPerHour">
                            <label for="costPerHour" class="active">Cost per Print Hour</label>
                        </div>
                        <div class="input-field col s6 cost">
                            <input type="number"
                                  min="0"
                                  name="costPerKg"
                                  id="costPerKg"
                                  v-model="formData.costPerKg">
                            <label for="costPerKg" class="active">Cost per kg</label>
                        </div>
                    </div>
                    <div class="row">
                        <div class="radio-group col s6">
                            <div class="radio-item">
                                <input type="radio"
                                      name="multiplier"
                                      id="multiplier"
                                      :value="true"
                                      v-model="formData.multiplier">
                                <label for="multiplier">
                                    Multiplier
                                </label>
                            </div>
                            <div class="radio-item">
                                <input type="radio"
                                      name="multiplier"
                                      id="flat"
                                      :value="false"
                                      v-model="formData.multiplier">
                                <label for="flat">
                                    Flat Dollar Amount
                                </label>
                            </div>
                        </div>
                        <div class="input-field col s6" v-if="formData.multiplier">
                            <input type="number"
                                  name="overhead"
                                  id="overhead"
                                  step="0.1"
                                  min="0"
                                  v-model="formData.overhead">
                            <label for="overhead" class="active">Overhead and Profit</label>
                        </div>
                        <div class="input-field col s6 cost" v-if="!formData.multiplier">
                            <input type="number"
                                  name="overheadFlat"
                                  id="overheadFlat"
                                  step="0.1"
                                  v-model="formData.overheadFlat">
                            <label for="overhead" class="active">Overhead and Profit</label>
                        </div>
                        <div class="error" v-if="validationError">
                            {{validationError}}
                        </div>
                    </div>
                    <template v-if="estimates.length">
                        <div class="row col">
                            <table class="estimate-table">
                                <tr>
                                    <th rowspan="3">Filename</th>
                                    <th rowspan="3">Objective Function</th>
                                    <th colspan="12" class="distortion">Max Distortion, mm</th>
                                    <th rowspan="3">Material Required, kg</th>
                                    <th rowspan="3">Print Time, h</th>
                                    <th rowspan="3">Material Cost, $</th>
                                    <th rowspan="3">Time Cost, $</th>
                                    <th rowspan="3">Total Cost, $</th>
                                </tr>
                                <tr>
                                  <th colspan="4">On-supp</th>
                                  <th colspan="4">Off-supp</th>
                                  <th colspan="4">Stress</th>
                                </tr>
                                <tr>
                                    <th class="distortion">X</th>
                                    <th class="distortion">Y</th>
                                    <th class="distortion">Z</th>
                                    <th class="distortion">Normal</th>
                                    <th class="distortion">X</th>
                                    <th class="distortion">Y</th>
                                    <th class="distortion">Z</th>
                                    <th class="distortion">Normal</th>
                                    <th class="distortion">X</th>
                                    <th class="distortion">Y</th>
                                    <th class="distortion">Z</th>
                                    <th class="distortion">Normal</th>
                                </tr>
                                <tr v-for="estimate in estimates" :key="estimate.jobOutputPathKey" class="estimate-row">
                                    <td>
                                        <div class="file">
                                            <i class="material-icons a3d color btn-delete-estimate"
                                                @click.prevent="deleteJobFromEstimate(estimate.jobOutputPathKey)">
                                                clear
                                            </i>
                                            <div class="filename">{{estimate.filename}}</div>
                                        </div>
                                    </td>
                                    <td>{{estimate.objectiveFunc}}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'on_x') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'on_y') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'on_z') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'on_normal') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'off_x') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'off_y') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'off_z') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'off_normal') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'stress_x') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'stress_y') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'stress_z') }}</td>
                                    <td class="distortion">{{ formatDistortion(estimate.distortion, 'stress_normal') }}</td>
                                    <td>{{Number(estimate.M_material).toFixed(3)}}</td>
                                    <td>{{Number(estimate.build_time).toFixed(3)}}</td>
                                    <td>{{totalCostMaterialPerEstimate(estimate)}}</td>
                                    <td>{{totalCostTimePerEstimate(estimate)}}</td>
                                    <td>{{costPerEstimate(estimate)}}</td>
                                </tr>
                            </table>
                        </div>
                    </template>
                    <template v-for="estimate in nestedEstimates">
                        <div class="row col" :key="`wrapper-${estimate.jobOutputPathKey}`">
                            <div class="nested-job-name" :key="`name-${estimate.jobOutputPathKey}`">
                                <i class="material-icons a3d color btn-delete-estimate"
                                    @click.prevent="deleteJobFromEstimate(estimate.jobOutputPathKey)">
                                    clear
                                </i>
                                {{estimate.filename}}
                            </div>
                            <template v-for="(plate, index) in estimate.plates">
                                <div class="nested-part-name" :key="`plate-number-${index}-${estimate.jobOutputPathKey}`">Plate {{index + 1}}</div>
                                <table :key="`plate-${index}-${estimate.jobOutputPathKey}`" class="estimate-table nested-table">
                                    <tr>
                                        <th rowspan="2">Part</th>
                                        <th rowspan="2">Quantity</th>
                                        <th colspan="3" class="distortion">On-plate distortion</th>
                                        <th colspan="3" class="distortion">Off-plate distortion</th>
                                        <th rowspan="2">Relative Completion Time, h</th>
                                        <th rowspan="2">Part max Z (mm)</th>
                                        <th colspan="4">X-Y Bounding Box (mm from 0,0)</th>
                                    </tr>
                                    <tr>
                                        <th class="distortion">X</th>
                                        <th class="distortion">Y</th>
                                        <th class="distortion">Z</th>
                                        <th class="distortion">X</th>
                                        <th class="distortion">Y</th>
                                        <th class="distortion">Z</th>
                                        <th class="distortion">Min X</th>
                                        <th class="distortion">Max X</th>
                                        <th class="distortion">Min Y</th>
                                        <th class="distortion">Max Y</th>
                                    </tr>
                                    <template v-for="(part, partIndex) in plate">
                                        <tr :key="`part-${partIndex}-${estimate.jobOutputPathKey}`">
                                            <td>{{part.filename}}</td>
                                            <td>{{part.count}}</td>
                                            <td class="distortion">{{ formatDistortion(part.distortion, 'on_x') }}</td>
                                            <td class="distortion">{{ formatDistortion(part.distortion, 'on_y') }}</td>
                                            <td class="distortion">{{ formatDistortion(part.distortion, 'on_z') }}</td>
                                            <td class="distortion">{{ formatDistortion(part.distortion, 'off_x') }}</td>
                                            <td class="distortion">{{ formatDistortion(part.distortion, 'off_y') }}</td>
                                            <td class="distortion">{{ formatDistortion(part.distortion, 'off_z') }}</td>
                                            <td>{{(part.buildTime && part.buildTime[0].toFixed(3)) || part.build_time.toFixed(3)}}</td>
                                            <td>{{part.distortion.partMaxZ ? part.distortion.partMaxZ : part.distortion.partHeight + part.distortion.offsetZ}}</td>
                                            <td>{{part.distortion.bounding_min[0]}}</td>
                                            <td>{{part.distortion.bounding_max[0]}}</td>
                                            <td>{{part.distortion.bounding_min[1]}}</td>
                                            <td>{{part.distortion.bounding_max[1]}}</td>
                                        </tr>
                                    </template>
                                    <br/>
                                    <tr class="plate-estimate">
                                        <td colspan="4"></td>
                                        <th colspan="4">Print Time, h:</th>
                                        <td>{{Number(estimate.timePerPlate[index]).toFixed(3)}}</td>
                                        <th>Material Consumed, kg:</th>
                                        <td colspan="4">{{Number(estimate.materialPerPlate[index]).toFixed(3)}}</td>
                                    </tr>
                                </table>
                            </template>
                            <div class="total-estimate">
                                <div>Total Print Time:</div>
                                <div>{{Number(estimate.build_time).toFixed(3)}} h</div>
                                <div>Total Material Consumed:</div>
                                <div>{{Number(estimate.M_material).toFixed(3)}} kg</div>
                                <div>Total Cost for Nested Job:</div>
                                <div>$ {{costPerEstimate(estimate)}}</div>
                            </div>
                        </div>
                    </template>
                </form>
            </div>
            <div class="modal-content" v-else>
                <div class="modal-header">Estimate Print Costs</div>
                <form class="modal-form">
                    <div class="row">
                        <div class="input-field col s6">
                            <input type="text"
                                  name="totalPrintTime"
                                  id="totalPrintTime"
                                  disabled
                                  :value="printTime">
                            <label for="totalPrintTime" class="active">Total Print Time</label>
                        </div>
                        <div class="input-field col s6 cost">
                            <input type="number"
                                  min="0"
                                  name="costPerHour"
                                  id="costPerHour"
                                  v-model="formData.costPerHour">
                            <label for="costPerHour" class="active">Cost per Print Hour</label>
                        </div>
                    </div>
                    <div class="row">
                        <div class="input-field col s6">
                            <input type="text"
                                  name="totalMaterial"
                                  id="totalMaterial"
                                  disabled
                                  :value="totalMaterial">
                            <label for="totalMaterial" class="active">Total Material Required</label>
                        </div>
                        <div class="input-field col s6 cost">
                            <input type="number"
                                  min="0"
                                  name="costPerKg"
                                  id="costPerKg"
                                  v-model="formData.costPerKg">
                            <label for="costPerKg" class="active">Cost per kg</label>
                        </div>
                    </div>
                    <div class="row">
                        <div class="radio-group col s6">
                            <div class="radio-item">
                                <input type="radio"
                                      name="multiplier"
                                      id="multiplier"
                                      :value="true"
                                      v-model="formData.multiplier">
                                <label for="multiplier">
                                    Multiplier
                                </label>
                            </div>
                            <div class="radio-item">
                                <input type="radio"
                                      name="multiplier"
                                      id="flat"
                                      :value="false"
                                      v-model="formData.multiplier">
                                <label for="flat">
                                    Flat Dollar Amount
                                </label>
                            </div>
                        </div>
                        <div class="input-field col s6" v-if="formData.multiplier">
                            <input type="number"
                                  name="overhead"
                                  id="overhead"
                                  step="0.1"
                                  min="0"
                                  v-model="formData.overhead">
                            <label for="overhead" class="active">Overhead and Profit</label>
                        </div>
                        <div class="input-field col s6 cost" v-if="!formData.multiplier">
                            <input type="number"
                                  name="overheadFlat"
                                  id="overheadFlat"
                                  step="0.1"
                                  v-model="formData.overheadFlat">
                            <label for="overhead" class="active">Overhead and Profit</label>
                        </div>
                    </div>
                    <div class="row" v-if="validationError">
                        <div class="error">
                            {{validationError}}
                        </div>
                    </div>
                    <div class="row">
                        <div class="total-label col s6">Total Print Cost</div>
                        <div class="input-field col s6 cost total">
                            <input type="text"
                                  name="totalCost"
                                  id="totalCost"
                                  disabled
                                  v-model="totalCost">
                        </div>
                    </div>
                </form>
            </div>
        </template>
    </modal>
</template>
<script>
  import { mapState, mapActions } from 'vuex'
  import { jobDescription, buildJobToCompare } from '../services/helpers'

  export default {
    data () {
      return {
        formData: {
          totalPrintTime: 0,
          costPerHour: 125,
          totalMaterial: 0,
          costPerKg: 200,
          multiplier: true,
          overhead: 1.8,
          overheadFlat: 100
        },
        isCompare: false,
        estimates: [],
        nestedEstimates: [],
        validationError: '',
        modalLoading: false
      }
    },
    computed: {
      ...mapState([
        'filesCompare',
        'nestedFilesCompare'
      ]),
      totalCost () {
        if (this.validateForm()) {
          let sum = (this.formData.totalPrintTime * this.formData.costPerHour +
            this.formData.totalMaterial * this.formData.costPerKg)
          if (this.formData.multiplier) {
            return (sum * this.formData.overhead).toFixed(2)
          } else {
            return (sum + parseFloat(this.formData.overheadFlat)).toFixed(2)
          }
        } else {
          return ''
        }
      },

      printTime () {
        return this.formData.totalPrintTime.toFixed(3) + ' hours'
      },
      totalMaterial () {
        return this.formData.totalMaterial.toFixed(3) + ' kg'
      }
    },
    methods: {
      ...mapActions([
        'getDownloadURL',
        'getURLData',
        'removeCompareData',
        'resetCompareFiles'
      ]),
      costPerEstimate (estimate) {
        if (this.validateForm()) {
          let sum = estimate.build_time * this.formData.costPerHour + estimate.M_material * this.formData.costPerKg
          if (this.formData.multiplier) {
            return (sum * this.formData.overhead).toFixed(2)
          } else {
            return (sum + parseFloat(this.formData.overheadFlat)).toFixed(2)
          }
        } else {
          return ''
        }
      },
      totalCostMaterialPerEstimate (estimate) {
        return (this.formData.costPerKg * estimate.M_material).toFixed(2)
      },
      totalCostTimePerEstimate (estimate) {
        return (this.formData.costPerHour * estimate.build_time).toFixed(2)
      },
      getCompareEstimates () {
        this.modalLoading = true
        const promises = []
        const nestedJobPromises = []
        const nestedPromises = []

        // get single job estimates
        Object.values(this.filesCompare).forEach(job => promises.push(this.createPromiseToGetData(job)))

        // get nested job estimates
        Object.values(this.nestedFilesCompare).forEach((job, index) => {
          nestedJobPromises.push(this.createPromiseToGetData(job))
          nestedPromises[index] = []

          // get the single jobs estimates of nested job
          job.nestedJobs.forEach(nestedJob => {
            const objToGetData = buildJobToCompare(nestedJob, nestedJob.jobOutputs[0])
            nestedPromises[index].push(this.createPromiseToGetData(objToGetData))
          })
        })

        Promise.all([
          Promise.all(promises),
          Promise.all(nestedJobPromises),
          Promise.all(nestedPromises.map(np => Promise.all(np)))
        ])
          .then(([estimates, nestedEstimates, nestedJobEstimates]) => {
            const nestedCombinedWithParts = nestedEstimates.map((nestedEstimate, index) => {
              const plates = []

              // combine data of plates of nested configuration and a separate job
              for (const part of nestedJobEstimates[index]) {
                nestedEstimate.plates.map((plate, plateIndex) => {
                  if (!plates[plateIndex]) {
                    plates[plateIndex] = []
                  }

                  for (const nestedPart of plate) {
                    const suffixes = [
                      '_manual_output',
                      '_support_volume_output',
                      '_support_removal_output',
                      '_build_time_output',
                      '_user_defined_output',
                      '_distortion_output',
                      '_time_distortion_output',
                      '_new_output'
                    ]
                    const replaceString = suffixes.join('|')
                    // eslint-disable-next-line no-useless-escape
                    const nestedFilename = nestedPart.file.replace(new RegExp(`(${replaceString})\.stl`), '.stl')
                    if (nestedFilename === part.filename) {
                      plates[plateIndex].push({...nestedPart, ...part})
                    }
                  }
                })
              }

              return {
                ...nestedEstimate,
                plates
              }
            })

            this.estimates = estimates
            this.nestedEstimates = nestedCombinedWithParts
            this.computedEstimates()
            this.modalLoading = false
          })
          .catch((error) => {
            console.log(error)
            this.modalLoading = false
          })
      },
      createPromiseToGetData (file) {
        const { config, sourceFilePathKey, jobPathKey, jobOutputPathKey, filename, outputFiles, analysisId, objectiveId, isNested, nestedJobName } = file
        const name = isNested ? nestedJobName : filename
        const costFile = isNested ? 'buildCostNesting.json' : 'cost.json'
        const objectiveFunc = jobDescription(analysisId, objectiveId, false, false, true)

        return new Promise((resolve, reject) => {
          this.getDownloadURL({ filename: costFile, sourceFilePathKey, jobPathKey, jobOutputPathKey })
            .then(response => {
              return this.getURLData({url: response.data.url})
            })
            .then(estimate => {
              if (isNested && config.calibrationFactor && config.calibrationFactor[0]) {
                estimate.data.build_time = estimate.data.build_time.map(t => {
                  return t * config.calibrationFactor[0]
                })
              }
              const { M_material, build_time, relative_build_time } = estimate.data
              const logFilename = this.checkLogFile(outputFiles)

              if (logFilename) {
                this.getDownloadURL({ filename: logFilename, sourceFilePathKey, jobPathKey, jobOutputPathKey })
                  .then(response => {
                    return this.getURLData({url: response.data.url})
                  })
                  .then(logFile => {
                    resolve({
                      filename: name,
                      jobOutputPathKey,
                      M_material: M_material.reduce((acc, current) => acc + current, 0).toFixed(6),
                      build_time: build_time.reduce((acc, current) => acc + current, 0).toFixed(6),
                      materialPerPlate: M_material.map(num => num.toFixed(6)),
                      timePerPlate: build_time.map(num => num.toFixed(6)),
                      objectiveFunc,
                      distortion: this.parseLogFile(logFile.data, isNested),
                      plates: this.parseNestedByPlates(logFile.data, relative_build_time, isNested)
                    })
                  })
              } else {
                resolve({
                  filename: name,
                  jobOutputPathKey,
                  M_material: M_material.reduce((acc, current) => acc + current, 0).toFixed(6),
                  build_time: build_time.reduce((acc, current) => acc + current, 0).toFixed(6),
                  objectiveFunc
                })
              }
            })
            .catch(() => {
              resolve({
                filename: name,
                jobOutputPathKey,
                hasError: true
              })
            })
        })
      },
      computedEstimates () {
        this.formData.totalPrintTime = 0
        this.formData.totalMaterial = 0
        for (let estimate of this.estimates) {
          if (!estimate.hasError) {
            this.formData.totalPrintTime += estimate.build_time
            this.formData.totalMaterial += estimate.M_material
          }
        }
        for (let estimate of this.nestedEstimates) {
          if (!estimate.hasError) {
            this.formData.totalPrintTime += estimate.build_time
            this.formData.totalMaterial += estimate.M_material
          }
        }
      },
      deleteJobFromEstimate (jobOutputPathKey) {
        this.removeCompareData(jobOutputPathKey)
        this.estimates = this.estimates.filter(elem => {
          return elem.jobOutputPathKey !== jobOutputPathKey
        })
        this.nestedEstimates = this.nestedEstimates.filter(elem => {
          return elem.jobOutputPathKey !== jobOutputPathKey
        })
        if (this.estimates.length < 1 && this.nestedEstimates.length < 1) {
          this.$modal.hide('estimate-cost')
        } else {
          this.computedEstimates()
        }
      },
      checkLogFile (array) {
        for (let file of array) {
          if (RegExp(/Log_(.+)_output\.txt|configNesting.json/).test(file.name)) {
            return file.name
          }
        }
        return false
      },
      parseNestedByPlates (data, relativeBuildTime, isNested) {
        if (isNested) {
          const nestingData = data.nesting2d ? data.nesting2d : data.nesting2dWithRotation
          const paramsByPart = data.nesting2d ? 2 : 3
          return nestingData.map((plate, index) => {
            const itemsOnPlate = []

            for (const [file, coordinates] of Object.entries(plate)) {
              const count = coordinates.length / paramsByPart
              let buildTime
              if (relativeBuildTime && relativeBuildTime[index]) {
                buildTime = relativeBuildTime[index][file]
              }
              itemsOnPlate.push({ file, count, buildTime })
            }
            return itemsOnPlate
          })
        }
        return false
      },
      parseLogFile (txt, isNested) {
        if (!isNested) {
          const dataRows = txt.match(/^(\s{0,}Max.*disp.*|Part bounding box.*|Part height.*|Part max Z.*|Z-offset.*).*/gm)
          const result = {}

          const parseRow = row => {
            const isPartMaxZ = row.match(/Part max Z/g)
            const isPartHeight = row.match(/Part height/g)
            const isOffsetZ = row.match(/Z-offset/g)

            let valueName, rowPart1, rowPart2
            if (isPartMaxZ) {
              valueName = 'partMaxZ'
            } else if (isPartHeight) {
              valueName = 'partHeight'
            } else if (isOffsetZ) {
              valueName = 'offsetZ'
            } else {
              rowPart1 = row.match(/(on|off|stress|bounding)/)
              rowPart2 = row.match(/\sx|y|z|normal|max|min(?= )/g)
              valueName = rowPart1[0] + '_' + rowPart2[0].trim()
            }

            const value = row.match(/-?\d+\.\d+/g)
            if (result.hasOwnProperty(valueName)) {
              return null
            }
            value.length <= 1 ? result[valueName] = Number(value) : result[valueName] = value.map(val => Number(val))
          }

          if (dataRows) {
            for (let row of dataRows) {
              if (row.match(/Part max Z|Part height|Z-offset|\sx|y|z|normal|bounding(?= )/g).length === 1) {
                parseRow(row)
              } else {
                const splitedRows = row.split(';')
                splitedRows.forEach(row => parseRow(row))
              }
            }
          } else {
            return false
          }

          return result
        }
        return false
      },
      formatDistortion (dist, type) {
        if (dist && typeof dist[type] === 'number') {
          return dist[type]
        } else {
          return '-'
        }
      },
      beforeOpen (event) {
        if (event.params.isCompare) {
          this.isCompare = true
          this.getCompareEstimates()
        } else {
          this.isCompare = false
          this.formData.totalPrintTime = event.params.data.build_time.reduce((a, b) => a + b)
          this.formData.totalMaterial = event.params.data.M_material.reduce((a, b) => a + b)
        }
      },
      opened (event) {
        this.$refs['estimate-cost'].setInitialSize()
      },
      validateForm () {
        let valid = false
        if (!this.formData.costPerHour || this.formData.costPerHour < 0) {
          this.validationError = 'Empty or incorrect cost per print hour'
        } else if (!this.formData.costPerKg || this.formData.costPerKg < 0) {
          this.validationError = 'Empty or incorrect cost per kg'
        } else if ((!this.formData.overhead || this.formData.overhead < 0) && this.formData.multiplier) {
          this.validationError = 'Empty or incorrect multiplier'
        } else if ((!this.formData.overheadFlat || this.formData.overheadFlat < 0) && !this.formData.multiplier) {
          this.validationError = 'Empty or incorrect profit overhead'
        } else {
          this.validationError = false
          valid = true
        }
        return valid
      }
    }
  }

</script>
<style lang="scss" scoped>
    .preloader {
        padding: 128px;
        margin: 0 auto;
        text-align: center;

        .spinner-layer {
            border-color: #00BDD1;
        }
    }
    .compare-mode {
        .estimate-table {
          padding: 20px;
          border-collapse: collapse;

          th, td {
            text-align: center;
            padding: 5px 10px;
            border-right: 1px dotted lightgray;
          }

          th:last-child, td:last-child {
            border-right: none;
          }

          tr {
            border-bottom: 1px dotted lightgray;

            &:last-child {
              border-bottom: none;
            }
          }

          .distortion {
            padding: 5px;
          }
          .file {
            display: flex;
            align-items: center;
            width: 100%;

            .filename {
              overflow-wrap: break-word;
              max-width: 200px;
              text-align: start;
              flex: 1 1 100%;
              padding: 0 10px;
            }
          }

          &.nested-table {
            .plate-estimate {
              &, td:not(:first-of-type), th {
                border: 1px dotted lightgray;
              }

              &, &:first-child {
                border: none;
              }
            }
          }
        }

        .total-estimate {
          display: flex;
          justify-content: space-between;
          border: 1px dotted gray;
          border-radius: 5px;
          margin-top: 20px;

          div {
            padding: 15px;
            text-align: center;

            &:not(:last-child) {
              border-right: 1px dotted lightgray;
            }

            &:nth-of-type(2n + 1) {
              font-weight: bold;
            }
          }
        }

        .btn-delete-estimate {
          font-size: 17px;
          cursor: pointer;
        }

        .nested-job-name {
          font-size: 18px;
          margin-top: 20px;
          font-weight: bold;
        }

        .nested-part-name {
          margin-top: 10px;
          font-weight: bold;
        }

        .estimate-row {
          td:first-child {
            padding-left: 0;
          }
        }

        .error {
          margin-bottom: 10px;
        }
    }

    .modal-content {
        padding: 16px;

        .modal-header {
            font-size: 20px;
            color: #000;
            padding-bottom: 16px;
        }

        .modal-form {
            .radio-group {
                .radio-item {
                    padding-bottom: 8px;

                    label {
                        color: #000;
                    }

                    [type="radio"]:checked + label:after,
                    [type="radio"].with-gap:checked + label:after {
                        background-color: #00BDD1;
                    }

                    [type="radio"]:checked + label:after,
                    [type="radio"].with-gap:checked + label:before,
                    [type="radio"].with-gap:checked + label:after {
                        border-color: #00BDD1;
                    }
                }
            }
            .input-field {
                &.cost {
                    &:before {
                        content: "$";
                        position: absolute;
                        top: 12px;
                    }

                    input {
                        text-indent: 14px;
                    }

                    &.total:before {
                        top: 9px;
                        font-size: 18px;
                    }

                    &.total {
                        input {
                            font-size: 18px;
                        }
                    }
                }

                label.active {
                    font-size: 16px;
                    color: #000;
                }
            }

            .total-label {
                padding-top: 24px;
                font-size: 18px;
            }

            .error {
                text-align: center;
                color: #ff0000;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// EstimateCost.vue?29e4395e